(() => {
  // Favoriler sayfası (sections/hp-favorites.liquid). Ramsey hp-favorites.js'ten port edildi:
  // Holly Wishlist API/state katmanı aynı, sepet katmanı Prestige (cart-drawer / cart-count) akışına uyarlandı.

  // Holly Wishlist app'inin misafir favorileri için kullandığı anahtar.
  const STORAGE_KEY = 'hollyWishListProducts';
  const IMAGE_WIDTHS = [360, 540, 720, 900, 1080];
  const PRODUCT_FETCH_CONCURRENCY = 4;
  // sections/hp-favorites-product.liquid
  const PRODUCT_SECTION_ID = 'hp-favorites-product';
  const REMOVE_CONCURRENCY = 4;
  const REMOVE_ANIMATION_MS = 240;
  const TOAST_MS = 5000;
  const SIZE_LABEL_OVERRIDES = { Standart: 'STD' };

  const productRequests = new Map();

  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  // "[count] ürün…" gibi section çevirilerindeki [anahtar] yer tutucularını doldurur.
  const formatMessage = (template, values = {}) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));

  // Promise.allSettled gibi sonuç döner; aynı anda en fazla `limit` görev çalışır.
  const settleWithConcurrency = async (list, limit, task) => {
    const results = new Array(list.length);
    let next = 0;
    const worker = async () => {
      while (next < list.length) {
        const index = next;
        next += 1;
        try {
          results[index] = { status: 'fulfilled', value: await task(list[index], index) };
        } catch (reason) {
          results[index] = { status: 'rejected', reason };
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(limit, list.length) }, worker));
    return results;
  };

  const routesRoot = () => window.Shopify?.routes?.root || '/';

  const readStoredFavorites = () => {
    try {
      const entries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(entries) ? entries.filter((entry) => entry?.productId) : [];
    } catch (error) {
      return [];
    }
  };

  const writeStoredFavorites = (entries) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
      console.error('[hp-favorites] Favoriler cihaza kaydedilemedi.', error);
    }
  };

  const toCents = (amount) => {
    const value = Number(amount);
    return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : 0;
  };

  // "Sepette" fiyatı; snippets/product-card-cart-price-badge.liquid ile aynı hesap.
  const cartPriceFor = (price, percent) => {
    const rate = Number(percent) || 0;
    if (!(rate > 0 && rate <= 100)) return 0;

    const cartPrice = Math.round((price * (100 - rate)) / 100);
    return cartPrice < price ? cartPrice : 0;
  };

  const absoluteUrl = (url) => (url && url.startsWith('//') ? `https:${url}` : url || '');

  const imageKey = (url) => absoluteUrl(url).split('?')[0].replace(/^https?:/, '');

  const resizeImage = (url, width) => {
    const src = absoluteUrl(url);
    return src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '';
  };

  const formatAmount = (cents, precision, thousands, decimal) => {
    const [integer, fraction] = (cents / 100).toFixed(precision).split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  const MONEY_FORMATS = {
    amount: (cents) => formatAmount(cents, 2, ',', '.'),
    amount_no_decimals: (cents) => formatAmount(cents, 0, ',', '.'),
    amount_with_comma_separator: (cents) => formatAmount(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: (cents) => formatAmount(cents, 0, '.', ','),
    amount_with_apostrophe_separator: (cents) => formatAmount(cents, 2, "'", '.'),
    amount_with_space_separator: (cents) => formatAmount(cents, 2, ' ', ','),
    amount_no_decimals_with_space_separator: (cents) => formatAmount(cents, 0, ' ', ','),
    amount_with_period_and_space_separator: (cents) => formatAmount(cents, 2, ' ', '.'),
  };

  const formatShopMoney = (cents, format) =>
    (format || '{{amount_with_comma_separator}} TL').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) =>
      (MONEY_FORMATS[key] || MONEY_FORMATS.amount)(Number(cents) || 0)
    );

  const productUrl = (handle, variantId) =>
    `${routesRoot()}products/${handle}${variantId ? `?variant=${variantId}` : ''}`;

  const buildLoginUrl = (loginUrl) => {
    if (!loginUrl) return '';
    const returnTo = window.location.pathname + window.location.search;
    return `${loginUrl}${loginUrl.includes('?') ? '&' : '?'}return_to=${encodeURIComponent(returnTo)}`;
  };

  const readPageQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const after = params.get('after');
    if (after) return `after=${encodeURIComponent(after)}`;

    const before = params.get('before');
    return before ? `before=${encodeURIComponent(before)}` : '';
  };

  const updatePageUrl = (key, cursor, { replace = false } = {}) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('after');
    url.searchParams.delete('before');
    if (key && cursor) url.searchParams.set(key, cursor);
    window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
  };

  // --- Holly Wishlist API ---------------------------------------------------

  const endpoint = (key) => {
    const url = window.HC_API?.[key];
    if (!url) throw new Error(`HC_API.${key} tanımlı değil. Holly-Constants.js yüklendi mi kontrol edin.`);
    return url;
  };

  const request = async (url, { method = 'GET', body } = {}) => {
    const response = await fetch(url, {
      method,
      headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) throw new Error(`${method} ${url} → HTTP ${response.status}`);

    // Başarılı ama gövdesiz yanıt (ör. remove) hata sayılmaz.
    const text = await response.text();
    const result = text ? JSON.parse(text) : {};
    // Holly hataları HTTP 200 + { isSuccess: false, statusCode: 'BadRequest' | 'NotFound' | ... } döner.
    if (result?.isSuccess === false) {
      const error = new Error(result.message || `${method} ${url} başarısız.`);
      error.statusCode = result.statusCode;
      throw error;
    }
    return result;
  };

  // Favorisi olmayan üyede favorite/page ve favorite/count boş sonuç yerine NotFound döner.
  const emptyWhenNotFound = (emptyValue) => (error) => {
    if (error?.statusCode === 'NotFound') return emptyValue;
    throw error;
  };

  const wishlistApi = {
    page: (query) =>
      request(`${endpoint('WISHLIST_PAGE')}${query ? `?${query}` : ''}`)
        .then((result) => result?.data)
        .catch(emptyWhenNotFound({ items: [], pageInfo: null })),
    count: () =>
      request(endpoint('WISHLIST_COUNT'))
        .then((result) => Number(result?.data) || 0)
        .catch(emptyWhenNotFound(0)),
    productList: (products) =>
      request(endpoint('WISHLIST_PRODUCT_LIST'), { method: 'POST', body: { products } }).then((result) => result?.data),
    remove: (productId, productVariantId) =>
      request(endpoint('WISHLIST_REMOVE'), { method: 'DELETE', body: { productId, productVariantId } }),
    addBulk: (products, customer) =>
      request(endpoint('WISHLIST_ADD_BULK'), {
        method: 'POST',
        body: { products, name: customer?.name || null, email: customer?.email || null },
      }),
  };

  // --- Wishlist kayıtları → kart state'i -------------------------------------

  const toItem = (entry) => {
    const product = entry?.product;
    if (!product?.shopifyId || !product.handle) return null;

    const productId = String(product.shopifyId);
    const variantId = product.variantShopifyId ? String(product.variantShopifyId) : null;
    const variantCount = Number(product.variantCount) || 1;
    const variantInStock = Number(product.inventoryStock) > 0;
    const price = toCents(product.price);
    const compareAtPrice = toCents(product.compareAtPrice);

    return {
      key: productId,
      productId,
      variantId,
      variantIds: [variantId],
      handle: product.handle,
      title: product.title || '',
      url: productUrl(product.handle, variantCount > 1 && variantInStock ? variantId : null),
      image: absoluteUrl(product.imageUrl),
      secondaryImage: '',
      price,
      compareAtPrice: compareAtPrice > price ? compareAtPrice : 0,
      // Sepette indirim oranı ürün verisiyle gelir (applyProduct).
      cartPrice: 0,
      // Holly stoğu yalnız kaydedilen varyantı söyler; tüm ürün durumu Shopify ürün JSON'u gelince netleşir.
      soldOut: variantCount <= 1 && !variantInStock,
      singleVariant: variantCount <= 1,
      defaultVariantId: variantInStock ? variantId : null,
      sizes: [],
      hasSizePicker: false,
      productState: 'idle',
      quickLoading: false,
      addingVariantId: null,
      // "Seçilenleri sepete ekle" için kartta seçtirilen beden.
      chosenVariantId: null,
      chosenSizeLabel: '',
      removing: false,
      removed: false,
    };
  };

  // Aynı ürünün farklı varyantları ayrı kayıt olabilir (kartta ilk varyant, PDP'de seçili beden);
  // tek kartta toplanır, çıkarırken tüm kayıtlar silinir.
  const toItems = (entries) => {
    const items = new Map();

    entries.forEach((entry) => {
      const item = toItem(entry);
      if (!item) return;

      const existing = items.get(item.key);
      if (!existing) {
        items.set(item.key, item);
      } else if (!existing.variantIds.includes(item.variantId)) {
        existing.variantIds.push(item.variantId);
      }
    });

    return Array.from(items.values());
  };

  // Ürün JSON'u + "Sepette" indirim oranı (metafield; `.js` uç noktası döndürmez).
  const fetchProductSection = async (handle) => {
    const response = await fetch(`${routesRoot()}products/${handle}?section_id=${PRODUCT_SECTION_ID}`, {
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const node = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector('[data-hp-favorites-product]');
    const data = JSON.parse(node?.textContent || 'null');
    if (!data?.product) throw new Error('Ürün verisi boş');

    return { ...data.product, cartDiscountPercent: Number(data.cartDiscountPercent) || 0 };
  };

  const fetchProductJson = async (handle) => {
    const response = await fetch(`${routesRoot()}products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };

  const fetchProduct = (handle) => {
    if (!productRequests.has(handle)) {
      // Section yanıt vermezse (404: ürün yayında değil ya da section yok) `.js` uç noktasına düşülür;
      // kart yine çalışır, yalnız "Sepette" fiyatı gösterilmez.
      const pending = fetchProductSection(handle)
        .catch(() => fetchProductJson(handle))
        .catch((error) => {
          productRequests.delete(handle);
          throw error;
        });

      productRequests.set(handle, pending);
    }

    return productRequests.get(handle);
  };

  // Beden listesi, kaydedilen varyantın diğer seçeneklerini (renk vb.) sabit tutar;
  // koleksiyon kartındaki hızlı beden seçici ile aynı mantık (theme.js ProductCardQuickVariantSelector).
  // Favorilerde yalnız stokta olan bedenler listelenir (QA R26).
  const buildSizes = (product, variants, baseVariant, sizeLabels) => {
    if (variants.length <= 1) return [];

    const options = product.options || [];
    const sizeIndex = options.findIndex((option) =>
      sizeLabels.includes(String(option?.name ?? option).trim().toLowerCase())
    );
    if (sizeIndex === -1) return [];

    const values =
      options[sizeIndex]?.values || [...new Set(variants.map((variant) => variant.options[sizeIndex]))];

    const sizes = values.map((value) => {
      const variant = variants.find(
        (candidate) =>
          candidate.options[sizeIndex] === value &&
          candidate.options.every((option, index) => index === sizeIndex || option === baseVariant?.options[index])
      );

      return {
        value,
        label: SIZE_LABEL_OVERRIDES[value] || value,
        variantId: variant?.id || null,
        available: Boolean(variant?.available),
      };
    });

    return sizes.filter((size) => size.available && size.variantId);
  };

  // "Seçilenleri sepete ekle": her ürün için eklenecek varyantı çözer.
  // Bedenli üründe Holly kaydındaki varyant (karttan eklenende ilk varyant) kullanılmaz; beden seçtirilir (QA R27).
  // Stokta tek beden kaldıysa o beden otomatik seçili sayılır.
  const planBulkAdd = (items, loaded) =>
    items.reduce(
      (plan, item, index) => {
        if (!loaded[index]) {
          plan.failed.push(item);
        } else if (item.soldOut) {
          plan.soldOut.push(item);
        } else if (item.singleVariant) {
          if (item.defaultVariantId) plan.ready.push({ item, variantId: item.defaultVariantId });
          else plan.soldOut.push(item);
        } else if (item.hasSizePicker) {
          const chosen =
            item.sizes.find((size) => size.variantId === item.chosenVariantId) ||
            (item.sizes.length === 1 ? item.sizes[0] : null);
          if (chosen) plan.ready.push({ item, variantId: chosen.variantId });
          else plan.needsSize.push(item);
        } else {
          // Ürün stokta ama kaydedilen renk/kalıbın bedeni yok (veya beden seçeneği yok):
          // tekil quick-add gibi seçim ürün sayfasında yapılır, "tükendi" sayılmaz (Ramsey ile aynı).
          plan.needsPdp.push(item);
        }
        return plan;
      },
      { ready: [], needsSize: [], soldOut: [], needsPdp: [], failed: [] }
    );

  const applyProduct = (item, product, sizeLabels) => {
    item.productState = 'ready';

    // Yayından kaldırılmış ürün.
    if (!product) {
      item.soldOut = true;
      return;
    }

    const variants = (product.variants || []).map((variant) => ({
      id: String(variant.id),
      available: Boolean(variant.available),
      options: [variant.option1, variant.option2, variant.option3].filter((option) => option != null),
      price: Number(variant.price) || 0,
      compareAtPrice: Number(variant.compare_at_price) || 0,
    }));
    const savedVariant = variants.find((variant) => variant.id === item.variantId);
    const firstAvailable = variants.find((variant) => variant.available);
    const baseVariant = savedVariant || firstAvailable || variants[0];

    item.soldOut = !product.available || !firstAvailable;
    item.singleVariant = variants.length <= 1;
    item.defaultVariantId = (savedVariant?.available ? savedVariant : firstAvailable)?.id || null;
    item.sizes = buildSizes(product, variants, baseVariant, sizeLabels);
    item.hasSizePicker = item.sizes.length > 0;
    item.url = productUrl(item.handle, variants.length > 1 && savedVariant?.available ? savedVariant.id : null);

    if (baseVariant) {
      item.price = baseVariant.price;
      item.compareAtPrice = baseVariant.compareAtPrice > baseVariant.price ? baseVariant.compareAtPrice : 0;
    }
    item.cartPrice = cartPriceFor(item.price, product.cartDiscountPercent);

    const images = (product.images || []).map(absoluteUrl);
    if (!item.image && images[0]) item.image = images[0];
    item.secondaryImage = images.find((src) => imageKey(src) !== imageKey(item.image)) || '';
  };

  // --- Tema sepeti (Prestige: cart-drawer + cart-count) ------------------------------

  // Sepet çekmecesi yeniden çizilecek section'larını bu event ile bildirir (theme.js ProductForm ile aynı).
  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return [...new Set(sections)].filter(Boolean);
  };

  // Temanın üst yükleme çubuğu (LoadingBar).
  const setThemeLoading = (loading) => {
    document.documentElement.dispatchEvent(
      new CustomEvent(loading ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  };

  const addVariantToCart = async (variantId) => {
    const sections = bundledSections();
    const response = await fetch(`${routesRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        ...(sections.length ? { sections: sections.join(','), sections_url: `${routesRoot()}cart` } : {}),
      }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || `HTTP ${response.status}`);
      // Shopify'ın 422 açıklaması (stok limiti vb.) kullanıcıya gösterilebilir.
      error.userMessage = typeof data.description === 'string' ? data.description : '';
      throw error;
    }
    return data;
  };

  // theme.js ProductForm'un başarı akışı: güncel cart.js + eklemenin döndürdüğü section HTML'i
  // 'cart:change' ile yayınlanır; cart-drawer içeriğini yeniler ve açılır (force_open_drawer),
  // cart-count sayacı item_count'a göre güncellenir.
  // Toplu eklemede `data` son başarılı yanıttır (çekmece HTML'i tüm eklemeleri içerir).
  const syncThemeCart = async (data) => {
    const settings = window.themeVariables?.settings || {};
    if (settings.cartType === 'page' || settings.pageType === 'cart') {
      window.location.href = `${routesRoot()}cart`;
      return;
    }

    try {
      const response = await fetch(`${routesRoot()}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cart = await response.json();
      cart.sections = data?.sections;
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart },
        })
      );
    } catch (error) {
      // Ürün eklendi ama sepet okunamadı: çekmece ve sayaç kendi kendini sunucudan yeniler.
      console.error('[hp-favorites] Sepet bilgisi alınamadı.', error);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  };

  // --- Alpine bileşeni -----------------------------------------------------------

  function registerHpFavoritesPage() {
    window.Alpine.data('hpFavoritesPage', () => ({
      config: { customer: null, i18n: {}, sizeLabels: [] },
      status: 'loading',
      items: [],
      pageInfo: null,
      pageQuery: '',
      total: 0,
      isRefreshing: false,
      activePickerKey: null,
      pickerOffset: 0,
      pickerMaxOffset: 0,
      pickerStep: 0,
      // 'add': beden seçilince sepete ekler; 'choose': toplu ekleme için bedeni kaydeder.
      pickerMode: 'add',
      sizeQueue: [],
      selectedKeys: [],
      bulkBusy: false,
      bulkMessage: '',
      confirmItems: [],
      confirmTrigger: null,
      announcement: '',
      toastMessage: '',
      toastTimer: 0,
      loadToken: 0,
      guestSyncDone: false,

      init() {
        this.config = this.readConfig();

        if (!this.isCustomer && !this.allowGuestFavorites) {
          window.location.assign(this.loginHref);
          return;
        }

        this.pageQuery = this.isCustomer ? readPageQuery() : '';
        this.load();

        window.addEventListener('popstate', () => {
          if (!this.isCustomer) return;
          this.pageQuery = readPageQuery();
          this.load();
        });
        // Holly app bloğunun dinlediği yenileme event'i; başka tetikleyenler için korunur.
        window.addEventListener('wishlist-page:update', () => this.load({ silent: true }));
        window.addEventListener('storage', (event) => {
          if (event.key === STORAGE_KEY && !this.isCustomer) this.load({ silent: true });
        });
        document.addEventListener('click', (event) => {
          // Toplu ekleme butonu beden seçiciyi kendisi açar; aynı tıklama onu kapatmamalı.
          if (this.activePickerKey && !event.target.closest('[data-hp-favorites-picker], [data-hp-favorites-toolbar]')) {
            this.closePicker();
          }
        });
      },

      readConfig() {
        try {
          const config = JSON.parse(this.$el.querySelector('[data-hp-favorites-config]')?.textContent || '{}');
          return {
            ...config,
            i18n: config.i18n || {},
            sizeLabels: String(config.sizeLabels || '')
              .split(',')
              .map((label) => label.trim().toLowerCase())
              .filter(Boolean),
          };
        } catch (error) {
          console.error('[hp-favorites] Section ayarları okunamadı.', error);
          return { customer: null, i18n: {}, sizeLabels: [] };
        }
      },

      get isCustomer() {
        return Boolean(this.config.customer?.id);
      },

      // Holly app embed'inin "üyeliksiz favori" ayarı (global const). Embed yoksa misafire izin ver.
      get allowGuestFavorites() {
        return typeof favoriteWithoutMembership === 'boolean' ? favoriteWithoutMembership : true;
      },

      get loginHref() {
        return buildLoginUrl(this.config.loginUrl);
      },

      get showGuestNotice() {
        return !this.isCustomer && this.status === 'ready' && this.items.length > 0;
      },

      get countLabel() {
        if (this.status !== 'ready' || this.total < 1) return '';
        return formatMessage(this.config.i18n.count || '[count]', { count: this.total });
      },

      get hasPagination() {
        return Boolean(this.pageInfo?.hasPreviousPage || this.pageInfo?.hasNextPage);
      },

      // --- Çoklu seçim (yalnız mevcut sayfadaki kartlar) ---

      get selectableItems() {
        return this.items.filter((item) => !item.removed);
      },

      get selectedItems() {
        return this.selectableItems.filter((item) => this.selectedKeys.includes(item.key));
      },

      get hasSelection() {
        return this.selectedItems.length > 0;
      },

      get isAllSelected() {
        const items = this.selectableItems;
        return items.length > 0 && items.every((item) => this.selectedKeys.includes(item.key));
      },

      get confirmText() {
        return formatMessage(this.config.i18n.removeConfirm, { count: this.confirmItems.length });
      },

      isSelected(item) {
        return this.selectedKeys.includes(item.key);
      },

      selectLabel(item) {
        return formatMessage(this.config.i18n.selectItem, { title: item.title });
      },

      isChoosingSize(item) {
        return this.pickerMode === 'choose' && this.isPickerOpen(item);
      },

      // Kart altı not: beden seçtirilirken "Lütfen bir beden seçin", seçildikten sonra "Beden: 48".
      sizeNote(item) {
        if (this.isChoosingSize(item)) return this.config.i18n.selectSize || '';
        // Tek stoklu beden toplu eklemede otomatik seçilir (planBulkAdd); kartta da gösterilir.
        const label = item.chosenSizeLabel || (item.hasSizePicker && item.sizes.length === 1 ? item.sizes[0].label : '');
        if (!label || !this.isSelected(item)) return '';
        return formatMessage(this.config.i18n.chosenSize, { size: label });
      },

      toggleSelected(item, selected = !this.isSelected(item)) {
        if (selected) {
          if (!this.isSelected(item)) this.selectedKeys = [...this.selectedKeys, item.key];
          return;
        }

        this.selectedKeys = this.selectedKeys.filter((key) => key !== item.key);
        item.chosenVariantId = null;
        item.chosenSizeLabel = '';
        if (this.pickerMode === 'choose' && this.sizeQueue.includes(item.key)) this.closePicker();
      },

      toggleSelectAll() {
        if (this.isAllSelected) {
          this.selectableItems.forEach((item) => this.toggleSelected(item, false));
        } else {
          this.selectedKeys = this.selectableItems.map((item) => item.key);
        }
      },

      // Liste yenilenince (sessiz yenileme, sayfa değişimi) artık görünmeyen kartlar seçimden düşer.
      pruneSelection() {
        this.selectedKeys = this.selectedKeys.filter((key) => this.selectableItems.some((item) => item.key === key));
      },

      async load({ silent = false } = {}) {
        const token = ++this.loadToken;
        const keepContent = silent && this.status === 'ready';

        if (keepContent) this.isRefreshing = true;
        else this.status = 'loading';

        try {
          const result = this.isCustomer ? await this.fetchCustomerFavorites() : await this.fetchGuestFavorites();
          if (token !== this.loadToken) return;

          this.closePicker();
          // Sessiz yenilemede kartta seçilmiş bedenler korunur.
          const previous = new Map(this.items.map((item) => [item.key, item]));
          result.items.forEach((item) => {
            const old = previous.get(item.key);
            if (old?.chosenVariantId) {
              item.chosenVariantId = old.chosenVariantId;
              item.chosenSizeLabel = old.chosenSizeLabel;
            }
          });
          this.items = result.items;
          this.pageInfo = result.pageInfo;
          this.total = result.total;
          this.status = 'ready';
          this.pruneSelection();
          this.hydrateProducts();
        } catch (error) {
          if (token !== this.loadToken) return;

          console.error('[hp-favorites] Favoriler yüklenemedi.', error);
          if (keepContent) this.showToast(this.config.i18n.loadError);
          else this.status = 'error';
        } finally {
          if (token === this.loadToken) this.isRefreshing = false;
        }
      },

      async fetchCustomerFavorites() {
        await this.syncGuestFavorites();

        const data = await wishlistApi.page(this.pageQuery);
        const entries = Array.isArray(data) ? data : data?.items || [];
        const pageInfo = Array.isArray(data) ? null : data?.pageInfo || null;
        const items = toItems(entries);

        // Boşalmış bir ara sayfaya (eski cursor) gelindiyse ilk sayfaya dön.
        if (!items.length && this.pageQuery) {
          this.pageQuery = '';
          updatePageUrl(null, null, { replace: true });
          return this.fetchCustomerFavorites();
        }

        const paginated = Boolean(pageInfo?.hasNextPage || pageInfo?.hasPreviousPage);
        const total = paginated ? await wishlistApi.count().catch(() => items.length) : items.length;

        return { items, pageInfo, total: Math.max(total, items.length) };
      },

      async fetchGuestFavorites() {
        const stored = readStoredFavorites();
        if (!stored.length) return { items: [], pageInfo: null, total: 0 };

        const data = await wishlistApi.productList(
          stored.map((entry) => ({
            productId: String(entry.productId),
            productVariantId: entry.productVariantId ? String(entry.productVariantId) : null,
          }))
        );
        const entries = Array.isArray(data) ? data : data?.items || [];

        // Endpoint sırayı korumuyor; en son eklenen en üstte olacak şekilde localStorage sırasına göre diz.
        const addedOrder = new Map();
        stored.forEach((entry, index) => addedOrder.set(String(entry.productId), index));
        const items = toItems(entries).sort(
          (a, b) => (addedOrder.get(b.productId) ?? -1) - (addedOrder.get(a.productId) ?? -1)
        );

        return { items, pageInfo: null, total: items.length };
      },

      // Giriş sonrası ilk sayfa favoriler olabilir (misafir bildirimi → login → return_to);
      // app embed'in window load'daki aktarımını beklemeden misafir favorilerini hesaba taşı.
      async syncGuestFavorites() {
        if (this.guestSyncDone) return;
        this.guestSyncDone = true;

        const stored = readStoredFavorites();
        if (!stored.length) return;

        // Önce temizle ki app embed aynı kayıtları ikinci kez göndermesin.
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          return;
        }

        try {
          await wishlistApi.addBulk(stored, this.config.customer);
        } catch (error) {
          console.error('[hp-favorites] Misafir favorileri hesaba aktarılamadı.', error);
          const addedMeanwhile = readStoredFavorites().filter(
            (entry) => !stored.some((item) => item.productVariantId === entry.productVariantId)
          );
          writeStoredFavorites([...stored, ...addedMeanwhile]);
        }
      },

      hydrateProducts() {
        const queue = this.items.filter((item) => item.productState === 'idle');
        const worker = async () => {
          while (queue.length) {
            await this.ensureProduct(queue.shift());
          }
        };

        for (let index = 0; index < Math.min(PRODUCT_FETCH_CONCURRENCY, queue.length); index += 1) {
          worker();
        }
      },

      async ensureProduct(item) {
        if (item.productState === 'ready') return true;

        item.productState = 'loading';
        try {
          applyProduct(item, await fetchProduct(item.handle), this.config.sizeLabels);
          return true;
        } catch (error) {
          console.error(`[hp-favorites] Ürün bilgisi alınamadı: ${item.handle}`, error);
          item.productState = 'idle';
          return false;
        }
      },

      isQuickAddBusy(item) {
        return item.quickLoading || (item.singleVariant && Boolean(item.addingVariantId));
      },

      async onQuickAdd(item) {
        if (item.soldOut || this.isQuickAddBusy(item)) return;

        if (this.isPickerOpen(item)) {
          this.closePicker();
          return;
        }

        if (item.productState !== 'ready') {
          item.quickLoading = true;
          const loaded = await this.ensureProduct(item);
          item.quickLoading = false;

          if (!loaded) {
            this.showToast(this.config.i18n.productError);
            return;
          }
          if (item.soldOut) return;
        }

        if (item.singleVariant) {
          await this.addToCart(item, item.defaultVariantId);
        } else if (item.hasSizePicker) {
          this.openPicker(item);
        } else {
          // Beden seçeneği olmayan çok varyantlı ürün: seçim PDP'de yapılır.
          window.location.assign(item.url);
        }
      },

      isPickerOpen(item) {
        return this.activePickerKey === item.key;
      },

      openPicker(item, mode = 'add') {
        if (mode !== 'choose') this.resetSizeQueue();
        this.pickerMode = mode;
        this.activePickerKey = item.key;
        this.pickerOffset = 0;
        this.pickerMaxOffset = 0;

        this.$nextTick(() => {
          const list = this.$root.querySelector('[data-hp-favorites-picker].is-open [data-size-list]');
          const viewport = list?.parentElement;
          if (!list || !viewport) return;

          // x-for'un <template>'i listenin ilk çocuğu; adım gerçek butonlardan ölçülür (boşluk dahil).
          const [first, second] = list.querySelectorAll('.product-card__quick-variant-button');
          this.pickerStep = first ? (second ? second.offsetTop - first.offsetTop : first.offsetHeight) : 0;
          this.pickerMaxOffset = Math.max(0, list.scrollHeight - viewport.clientHeight);

          if (mode !== 'choose') return;
          const card = list.closest('.hp-favorites__item');
          const rect = card?.getBoundingClientRect();
          if (rect && (rect.top < 0 || rect.bottom > window.innerHeight)) {
            card.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },

      closePicker() {
        this.activePickerKey = null;
        this.pickerOffset = 0;
        // Beden seçtirme sırasında seçici kapatılırsa toplu ekleme iptal edilir.
        this.resetSizeQueue();
      },

      resetSizeQueue() {
        this.pickerMode = 'add';
        this.sizeQueue = [];
        this.bulkMessage = '';
      },

      nextQueuedItem() {
        return this.sizeQueue
          .map((key) => this.selectedItems.find((item) => item.key === key))
          .find((item) => item && !item.soldOut && item.hasSizePicker);
      },

      onSizePick(item, size) {
        if (this.pickerMode !== 'choose') {
          this.addToCart(item, size.variantId);
          return;
        }

        item.chosenVariantId = size.variantId;
        item.chosenSizeLabel = size.label;
        this.sizeQueue = this.sizeQueue.filter((key) => key !== item.key);

        const next = this.nextQueuedItem();
        if (next) {
          this.openPicker(next, 'choose');
          return;
        }

        this.closePicker();
        this.addSelectedToCart();
      },

      scrollPicker(direction) {
        const step = this.pickerStep || 37;
        this.pickerOffset = Math.min(this.pickerMaxOffset, Math.max(0, this.pickerOffset + direction * step));
      },

      async addToCart(item, variantId) {
        if (!variantId || item.addingVariantId) return;

        item.addingVariantId = variantId;
        setThemeLoading(true);
        try {
          const data = await addVariantToCart(variantId);
          this.closePicker();
          await syncThemeCart(data);
          this.announce(this.config.i18n.added);
        } catch (error) {
          console.error('[hp-favorites] Ürün sepete eklenemedi.', error);
          this.showToast(error.userMessage || this.config.i18n.addError);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } finally {
          item.addingVariantId = null;
          setThemeLoading(false);
        }
      },

      // "Seçilenleri sepete ekle": bedeni belli olmayan ürünler için önce sırayla beden seçtirir,
      // ardından tüm ürünleri tek tek (422 bir ürünü düşürse de diğerleri eklensin diye) sepete ekler.
      async addSelectedToCart() {
        if (this.bulkBusy || !this.hasSelection) return;

        this.closePicker();
        this.bulkBusy = true;

        try {
          const selected = this.selectedItems;
          const loaded = await Promise.all(selected.map((item) => this.ensureProduct(item)));
          const plan = planBulkAdd(selected, loaded);

          if (plan.needsSize.length) {
            this.sizeQueue = plan.needsSize.map((item) => item.key);
            this.openPicker(plan.needsSize[0], 'choose');
            this.bulkMessage = this.config.i18n.chooseSizes || '';
            this.announce(this.bulkMessage);
            return;
          }

          const added = [];
          const failed = [...plan.failed];
          let lastData = null;
          let failureMessage = '';

          if (plan.ready.length) setThemeLoading(true);
          try {
            for (const { item, variantId } of plan.ready) {
              item.addingVariantId = variantId;
              try {
                lastData = await addVariantToCart(variantId);
                added.push(item);
              } catch (error) {
                console.error(`[hp-favorites] Ürün sepete eklenemedi: ${item.handle}`, error);
                failed.push(item);
                failureMessage = failureMessage || error.userMessage;
              } finally {
                item.addingVariantId = null;
              }
            }

            added.forEach((item) => {
              item.chosenVariantId = null;
              item.chosenSizeLabel = '';
            });
            this.selectedKeys = this.selectedKeys.filter((key) => !added.some((item) => item.key === key));

            if (lastData) await syncThemeCart(lastData);
            else if (plan.ready.length) document.dispatchEvent(new CustomEvent('cart:refresh'));
          } finally {
            if (plan.ready.length) setThemeLoading(false);
          }

          const { i18n } = this.config;
          const warnings = [];
          if (plan.soldOut.length) warnings.push(formatMessage(i18n.soldOutSkipped, { count: plan.soldOut.length }));
          if (plan.needsPdp.length) warnings.push(formatMessage(i18n.needsPdp, { count: plan.needsPdp.length }));
          if (failed.length) {
            warnings.push(
              failed.length === 1 && failureMessage
                ? failureMessage
                : formatMessage(i18n.addFailed, { count: failed.length })
            );
          }

          if (warnings.length) this.showToast(warnings.join(' '));
          this.announce(
            [added.length ? formatMessage(i18n.addedCount, { count: added.length }) : '', ...warnings]
              .filter(Boolean)
              .join(' ')
          );
        } finally {
          this.bulkBusy = false;
        }
      },

      remove(item) {
        return this.removeItems([item]);
      },

      requestRemoveSelected(event) {
        const items = this.selectedItems.filter((item) => !item.removing);
        if (this.bulkBusy || !items.length) return;

        this.closePicker();
        this.confirmItems = items;
        this.confirmTrigger = event?.currentTarget || document.activeElement;

        const dialog = this.$refs.confirmDialog;
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') {
          if (!dialog.open) dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        this.$nextTick(() => this.$refs.confirmOk?.focus());
      },

      closeConfirm() {
        const dialog = this.$refs.confirmDialog;
        if (!this.confirmItems.length && !dialog?.open) return;

        this.confirmItems = [];
        if (dialog?.open) {
          if (typeof dialog.close === 'function') dialog.close();
          else dialog.removeAttribute('open');
        }

        const trigger = this.confirmTrigger;
        this.confirmTrigger = null;
        if (trigger?.isConnected && !trigger.disabled) trigger.focus();
      },

      async confirmRemove() {
        const items = this.confirmItems;
        this.closeConfirm();
        if (!items.length || this.bulkBusy) return;

        this.bulkBusy = true;
        try {
          await this.removeItems(items);
        } finally {
          this.bulkBusy = false;
        }
      },

      async removeItems(items) {
        const targets = items.filter((item) => !item.removing && !item.removed);
        if (!targets.length) return;

        targets.forEach((item) => {
          item.removing = true;
        });
        if (targets.some((item) => this.isPickerOpen(item))) this.closePicker();

        let removed = targets;
        // hp-personaclick.js'in fetch sarmalayıcısı { isSuccess: true } dönen favorite/remove isteklerini
        // zaten bildirir; yalnız bildirilmeyenler (gövdesiz yanıt, misafir) aşağıda elle bildirilir.
        const reportedByFetch = new Set();
        if (this.isCustomer) {
          // Aynı ürünün tüm Holly kayıtları (farklı varyantlar) silinmeli; biri kalırsa ürün listede kalır.
          const results = await settleWithConcurrency(targets, REMOVE_CONCURRENCY, (item) =>
            Promise.all(item.variantIds.map((variantId) => wishlistApi.remove(item.productId, variantId)))
          );
          removed = targets.filter((item, index) => results[index].status === 'fulfilled');
          targets.forEach((item, index) => {
            const responses = results[index].status === 'fulfilled' ? results[index].value : [];
            if (responses.length && responses.every((response) => response?.isSuccess === true)) {
              reportedByFetch.add(item.key);
            }
          });

          const failed = targets.filter((item, index) => results[index].status === 'rejected');
          if (failed.length) {
            results
              .filter((result) => result.status === 'rejected')
              .forEach((result) => console.error('[hp-favorites] Ürün favorilerden çıkarılamadı.', result.reason));
            failed.forEach((item) => {
              item.removing = false;
            });
            this.showToast(this.config.i18n.removeError);
          }
        } else {
          const productIds = new Set(targets.map((item) => item.productId));
          writeStoredFavorites(readStoredFavorites().filter((entry) => !productIds.has(String(entry.productId))));
        }

        if (!removed.length) return;

        removed.forEach((item) => {
          if (!reportedByFetch.has(item.key)) {
            window.HpPersonaClick?.wishlistChanged?.({
              productId: item.productId,
              added: false,
              operationId: `hp-favorites:${item.productId}:${Date.now()}`,
            });
          }
          item.removed = true;
        });
        await wait(REMOVE_ANIMATION_MS);

        const removedKeys = new Set(removed.map((item) => item.key));
        this.items = this.items.filter((entry) => !removedKeys.has(entry.key));
        this.selectedKeys = this.selectedKeys.filter((key) => !removedKeys.has(key));
        this.total = Math.max(0, this.total - removed.length);
        this.announce(
          removed.length === 1
            ? this.config.i18n.removed
            : formatMessage(this.config.i18n.removedCount, { count: removed.length })
        );

        if (!this.isCustomer) return;

        // Sayfalı listede boşluğu sonraki sayfadan doldur; sayfa boşaldıysa başa dön.
        if (this.pageInfo?.hasNextPage) {
          this.load({ silent: true });
        } else if (!this.items.length && this.pageInfo?.hasPreviousPage) {
          this.pageQuery = '';
          updatePageUrl(null, null, { replace: true });
          this.load();
        }
      },

      goToPage(direction) {
        const info = this.pageInfo || {};
        const isNext = direction === 'next';
        const cursor = isNext
          ? info.hasNextPage && info.nextPageCursor
          : info.hasPreviousPage && info.previousPageCursor;
        if (!cursor) return;

        const key = isNext ? 'after' : 'before';
        updatePageUrl(key, cursor);
        this.pageQuery = `${key}=${encodeURIComponent(cursor)}`;
        this.load();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },

      announce(message) {
        this.announcement = '';
        this.$nextTick(() => {
          this.announcement = message || '';
        });
      },

      // Temada genel bir toast bileşeni yok; section içi bildirim (role="alert") kullanılır.
      showToast(message) {
        if (!message) return;

        window.clearTimeout(this.toastTimer);
        this.toastMessage = '';
        this.$nextTick(() => {
          this.toastMessage = message;
          this.toastTimer = window.setTimeout(() => this.hideToast(), TOAST_MS);
        });
      },

      hideToast() {
        window.clearTimeout(this.toastTimer);
        this.toastMessage = '';
      },

      formatMoney(cents) {
        return formatShopMoney(cents, this.config.moneyFormat);
      },

      imageUrl(url) {
        return resizeImage(url, 720);
      },

      imageSrcset(url) {
        return url ? IMAGE_WIDTHS.map((width) => `${resizeImage(url, width)} ${width}w`).join(', ') : '';
      },
    }));
  }

  if (window.Alpine) {
    registerHpFavoritesPage();
  } else {
    document.addEventListener('alpine:init', registerHpFavoritesPage);
  }
})();
